import { useForm } from 'react-hook-form';
import { useTranslations } from 'next-intl';
import TextInput from 'elements/TextInput';
import { Button } from 'elements/Button';
import classNames from 'classnames';

interface FormValues {
  email: string;
}

interface Props {
  /** Prefills the field, e.g. when coming back from a later step. */
  defaultEmail?: string;
  onSubmit: (email: string) => Promise<void>;
}

// Inline first step of the email sign-in: shown directly on the sign-in screen
// above the social providers, so getting a code is a single step.
const EmailForm: React.FC<Props> = ({ defaultEmail = '', onSubmit }) => {
  const t = useTranslations();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { email: defaultEmail } });

  return (
    <form
      onSubmit={handleSubmit(({ email }) => onSubmit(email))}
      className={styles.container}
    >
      <TextInput
        name="email"
        label={t('auth.email.label')}
        hideLabel
        aria-label={t('auth.email.label')}
        type="email"
        placeholder="you@example.com"
        errors={errors}
        rootClassName={styles.root}
        inputClassName={styles.input}
        data-1p-ignore
        data-lpignore
        {...register('email', { required: true })}
      />
      <Button
        type="submit"
        variant="solid"
        color="primary"
        size="md"
        fullWidth
        loading={isSubmitting}
        loadingText={t('auth.email.checking')}
        className={styles.submit}
      >
        {t('auth.continueWith.email')}
      </Button>
    </form>
  );
};

export default EmailForm;

const styles = {
  container: 'flex flex-col w-72 space-y-3 text-left',
  // The app's field reshaped into a white pill. Colours are pinned so it
  // stays white-on-light even when the app's theme class is dark.
  root: classNames(
    'rounded-full border-mf-gray-200 bg-white px-5 text-sm text-mf-off-black-600 shadow-none',
    'transition-all duration-200 focus-within:border-mf-blue-400 focus-within:ring-blue-100',
  ),
  input: 'placeholder:text-[#A4AEC2]',
  submit: 'rounded-full bg-mf-blue text-sm font-semibold hover:bg-mf-blue/90',
};
